import type { Node } from '../../models/scene';
import { resolveEffectiveShapeDimension } from '../../shapes/effectiveDimensions';

export type FiniteNodeRect = { x: number; y: number; w: number; h: number };

export type FiniteNodeRectResolution = {
  rect: FiniteNodeRect;
  renderable: boolean;
  invalidFields: string[];
  usedFallback: boolean;
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isPositiveFiniteNumber = (value: unknown): value is number =>
  isFiniteNumber(value) && value > 0;

export function isFiniteNodeRect(rect: FiniteNodeRect): boolean {
  return isFiniteNumber(rect.x)
    && isFiniteNumber(rect.y)
    && isPositiveFiniteNumber(rect.w)
    && isPositiveFiniteNumber(rect.h);
}

export function isNodeBelowNodeBounds(node: Node, parent: Node): boolean {
  const nodeRect = resolveFiniteNodeRect(node).rect;
  const parentRect = resolveFiniteNodeRect(parent).rect;
  return nodeRect.y > parentRect.y + parentRect.h;
}

export function resolveFiniteNodeRect(node: Node): FiniteNodeRectResolution {
  const record = node as unknown as Record<string, unknown>;
  const attrs = (record.attrs && typeof record.attrs === 'object'
    ? record.attrs
    : {}) as Record<string, unknown>;
  const invalidFields: string[] = [];

  const collectInvalid = (owner: Record<string, unknown>, key: string, label: string, positive = false) => {
    if (!Object.prototype.hasOwnProperty.call(owner, key)) return;
    const value = owner[key];
    if (positive ? !isPositiveFiniteNumber(value) : !isFiniteNumber(value)) invalidFields.push(label);
  };
  collectInvalid(record, 'x', 'x');
  collectInvalid(record, 'y', 'y');
  for (const key of ['width', 'height', 'w', 'h', 'rectW', 'rectH'] as const) {
    collectInvalid(record, key, key, true);
    collectInvalid(attrs, key, `attrs.${key}`, true);
  }
  collectInvalid(record, 'rectX', 'rectX');
  collectInvalid(record, 'rectY', 'rectY');
  collectInvalid(attrs, 'rectX', 'attrs.rectX');
  collectInvalid(attrs, 'rectY', 'attrs.rectY');

  const rectW = isPositiveFiniteNumber(record.rectW) ? record.rectW
    : isPositiveFiniteNumber(attrs.rectW) ? attrs.rectW : undefined;
  const rectH = isPositiveFiniteNumber(record.rectH) ? record.rectH
    : isPositiveFiniteNumber(attrs.rectH) ? attrs.rectH : undefined;
  const outerW = resolveEffectiveShapeDimension(record, 'width');
  const outerH = resolveEffectiveShapeDimension(record, 'height');
  const fallbackW = record.type === 'image' ? 240 : 60;
  const fallbackH = record.type === 'image' ? 160 : 60;
  const isRenderedPersonSymbol = record.type === 'person' && attrs.isPendingAI !== true;

  const preferredX = !isRenderedPersonSymbol && rectW !== undefined
    ? [record.rectX, attrs.rectX, record.x]
    : [record.x];
  const preferredY = !isRenderedPersonSymbol && rectH !== undefined
    ? [record.rectY, attrs.rectY, record.y]
    : [record.y];
  const selectedX = preferredX.find(isFiniteNumber);
  const selectedY = preferredY.find(isFiniteNumber);
  let x = selectedX ?? 0;
  let y = selectedY ?? 0;
  let w = rectW ?? outerW ?? fallbackW;
  let h = rectH ?? outerH ?? fallbackH;
  let hasRenderablePosition = selectedX !== undefined && selectedY !== undefined;

  if (isRenderedPersonSymbol) {
    const size = Math.max(1, Math.min(outerW ?? fallbackW, outerH ?? fallbackH));
    w = size;
    h = size;
  } else if (record.type === 'freehand') {
    const points = Array.isArray(attrs.points) ? attrs.points : [];
    const finitePairs: Array<{ x: number; y: number }> = [];
    for (let index = 0; index + 1 < points.length; index += 2) {
      const px = points[index];
      const py = points[index + 1];
      if (isFiniteNumber(px) && isFiniteNumber(py)) finitePairs.push({ x: px, y: py });
    }
    if (finitePairs.length >= 2 && isFiniteNumber(record.x) && isFiniteNumber(record.y)) {
      const xs = finitePairs.map((point) => point.x);
      const ys = finitePairs.map((point) => point.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      x = record.x + minX;
      y = record.y + minY;
      w = Math.max(1, maxX - minX);
      h = Math.max(1, maxY - minY);
      hasRenderablePosition = true;
    }
  }

  const rect = { x, y, w, h };
  return {
    rect,
    renderable: hasRenderablePosition && isFiniteNodeRect(rect),
    invalidFields,
    usedFallback:
      selectedX === undefined
      || selectedY === undefined
      || (
        isRenderedPersonSymbol
          ? outerW === undefined || outerH === undefined
          : (rectW === undefined && outerW === undefined) || (rectH === undefined && outerH === undefined)
      ),
  };
}

export function hasFiniteConnectorRouteGeometry(value: unknown): boolean {
  if (typeof value === 'number') return Number.isFinite(value);
  if (!value || typeof value !== 'object') return true;
  if (Array.isArray(value)) {
    if (value.some((entry) => typeof entry === 'number' || entry === null)) {
      return value.every(isFiniteNumber);
    }
    return value.every(hasFiniteConnectorRouteGeometry);
  }

  const record = value as Record<string, unknown>;
  for (const [key, fieldValue] of Object.entries(record)) {
    if (/^[xy](?:\d+)?$/.test(key) && !isFiniteNumber(fieldValue)) return false;
    if (!hasFiniteConnectorRouteGeometry(fieldValue)) return false;
  }
  return true;
}
